/**
 * RenewalPreviewModal — modal that previews a Phase 9E renewal PDF inline.
 *
 * Calls POST /api/renewal/generate/{document_type} which returns application/pdf.
 * Renders the blob in an object/iframe. Has Download button.
 */
import React, { useState } from "react";
import { Download, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import { apiClient, formatApiError } from "@/lib/apiClient";

export const RenewalPreviewModal = ({ open, debtorId, documentType, documentDisplayName, onClose }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open || !documentType || !debtorId) return;
    let revoked = false;
    setLoading(true);
    setPdfUrl(null);
    apiClient
      .post(`/renewal/generate/${documentType}`, { debtor_id: debtorId }, { responseType: "blob" })
      .then((res) => {
        if (revoked) return;
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        setPdfBlob(blob);
        setPdfUrl(url);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    return () => {
      revoked = true;
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentType, debtorId]);

  if (!open) return null;

  const download = () => {
    if (!pdfBlob) return;
    const url = pdfUrl || URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${documentType}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success("Downloaded — saved to your Reports tab as well.");
  };

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-[12px] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="renewal-preview-modal"
    >
      <div
        className="w-full max-w-[880px] max-h-[92vh] bg-bg-secondary border border-border-default rounded-[16px] shadow-elevated flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-border-default">
          <div>
            <div className="jea-caption">Renewal Document · Preview</div>
            <h3 className="text-white font-bold text-[18px]">{documentDisplayName}</h3>
            <div className="text-text-secondary text-[12px] mt-1">
              Pre-populated with this judgment's data. Review with a licensed attorney before filing.
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-text-secondary hover:text-white" data-testid="renewal-preview-close">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 bg-white overflow-hidden" data-testid="renewal-preview-body">
          {loading || !pdfUrl ? (
            <div className="w-full h-[540px] flex items-center justify-center text-text-secondary bg-bg-tertiary animate-pulse">
              Generating PDF…
            </div>
          ) : (
            <iframe title="Renewal Preview" src={pdfUrl} className="w-full h-[540px] block bg-white" />
          )}
        </div>
        <div className="flex gap-2 justify-end p-4 border-t border-border-default flex-wrap">
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="h-11 px-4 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[13px] font-semibold inline-flex items-center gap-2"
              data-testid="renewal-preview-open"
            >
              <ExternalLink size={13} /> Open in new tab
            </a>
          )}
          <JeaButton onClick={download} disabled={!pdfBlob} className="gap-2" data-testid="renewal-preview-download">
            <Download size={13} /> Download &amp; Save to Reports
          </JeaButton>
        </div>
      </div>
    </div>
  );
};

export default RenewalPreviewModal;
